import { useCallback, useEffect, useRef } from 'react';
import { UseEditLockProps } from '10.quickConnect.app/components/domain/Declaration/hooks/useEditLock/types';
import { acquireEditLockAsync } from '80.quickConnect.Core/helpers';

/**
 * Verrous d'édition de la saisie affichée, pour qu'elle ne soit modifiable que dans cet onglet.
 * Ils sont tenus jusqu'à la sortie de la saisie, ou jusqu'à la fermeture de l'onglet.
 */
const useEditLock = (): UseEditLockProps => {
  const releasesRef = useRef<Array<() => void>>([]);
  const isMountedRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      releasesRef.current.forEach((release) => release());
      releasesRef.current = [];
    };
  }, []);

  // Prend tous les verrous, ou aucun si l'un des verrous exclusifs est tenu par un autre onglet
  const lockAsync = useCallback(async (lockNames: string[], sharedLockNames: string[] = []): Promise<boolean> => {
    const releases = await Promise.all([
      ...lockNames.map((name) => acquireEditLockAsync(name)),
      ...sharedLockNames.map((name) => acquireEditLockAsync(name, 'shared')),
    ]);
    const acquired = releases.filter((release): release is () => void => !!release);

    // Un verrou tenu par un autre onglet, ou une saisie quittée pendant leur prise : aucun n'est gardé
    if (acquired.length < releases.length || !isMountedRef.current) {
      acquired.forEach((release) => release());
      return false;
    }

    releasesRef.current.push(...acquired);
    return true;
  }, []);

  return { lockAsync };
};

export default useEditLock;
